/**
 * Reload once when a lazy chunk fails to load.
 *
 * Every route in App.tsx is a React.lazy() import. After a deploy, a tab that
 * still holds the previous index.html, or a fetch that lands mid-swap, asks for
 * a chunk hash that no longer exists. The import rejects and the user gets the
 * "Page Error" card, whose Try Again re-imports the same dead URL. Seen live on
 * brazilnut.ai on 2026-09-02 as "Importing a module script failed."
 *
 * Vite wraps every dynamic import and dispatches a cancelable
 * `vite:preloadError` on window when one fails. This listener answers it with
 * one full reload, which fetches the current index.html and the chunks it
 * names. One reload per window: if the chunk is still missing after a fresh
 * load, something else is wrong and the error boundary should show it, not
 * loop. If the reload cannot be recorded (storage unavailable), no reload
 * happens, for the same reason.
 */

const STORAGE_KEY = 'brazilnut:chunk-reload-at'

/** How long after a reload a second chunk failure is treated as persistent. */
export const RELOAD_WINDOW_MS = 30_000

export interface ChunkReloadOptions {
  /** Where `vite:preloadError` is dispatched. Defaults to window. */
  target?: EventTarget
  /** Where the last reload time is recorded. Defaults to sessionStorage. */
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
  /** The reload itself. Defaults to window.location.reload(). */
  reload?: () => void
  /** Clock, for tests. */
  now?: () => number
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/**
 * Install the listener. Returns a function that removes it. Safe to call
 * during SSR: does nothing without a window.
 */
export function installChunkReload(options: ChunkReloadOptions = {}): () => void {
  if (typeof window === 'undefined') return () => {}

  const target = options.target ?? window
  const reload = options.reload ?? (() => window.location.reload())
  const now = options.now ?? Date.now
  const storage = options.storage === undefined ? defaultStorage() : options.storage

  const handler = (event: Event) => {
    if (!storage) return

    let last = 0
    try {
      last = Number(storage.getItem(STORAGE_KEY) ?? 0)
    } catch {
      return
    }
    if (Number.isFinite(last) && now() - last < RELOAD_WINDOW_MS) return

    try {
      storage.setItem(STORAGE_KEY, String(now()))
    } catch {
      return
    }

    event.preventDefault()
    reload()
  }

  target.addEventListener('vite:preloadError', handler)
  return () => target.removeEventListener('vite:preloadError', handler)
}
